import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MsalService } from '@azure/msal-angular';
import { fromEvent } from 'rxjs';
import { environment } from '../../../environment/environment';
import { AuthService } from './auth.service';
import { ReturnUrlService } from './return-url.service';

/**
 * Background watchdog that keeps long-lived sessions coherent across the
 * three idle hazards MsalInterceptor on its own cannot react to:
 *
 * - tab wake-up: an inactive tab can outlive its access token; the first
 *   click would race a 401. We call `acquireTokenSilent` on every
 *   `visibilitychange -> visible` so MSAL refreshes the cache before any
 *   real request goes out.
 * - network resume: same problem after a Wi-Fi drop. `online` triggers the
 *   same silent refresh.
 * - multi-tab logout: when another tab calls `logoutRedirect`, MSAL wipes
 *   its localStorage keys. We listen for `storage` events on those keys and
 *   drive the still-live tab through AuthService.logout so it shares the
 *   same teardown path as a deliberate sign-out.
 *
 * The service stays passive when the local tab has no active account (pre-
 * login or already logged out); there is nothing to refresh or to abandon.
 */
@Injectable({ providedIn: 'root' })
export class SessionWatcherService {
  private readonly msalService = inject(MsalService);
  private readonly authService = inject(AuthService);
  private readonly returnUrl = inject(ReturnUrlService);
  private readonly destroyRef = inject(DestroyRef);
  private initialized = false;

  init(): void {
    if (this.initialized || typeof window === 'undefined') return;
    this.initialized = true;

    fromEvent(document, 'visibilitychange')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (document.visibilityState === 'visible') this.refreshSilently();
      });

    fromEvent(window, 'online')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.refreshSilently());

    fromEvent<StorageEvent>(window, 'storage')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(event => this.handleStorageEvent(event));
  }

  private refreshSilently(): void {
    const account = this.msalService.instance.getActiveAccount();
    if (!account) return;
    this.msalService.acquireTokenSilent({ scopes: environment.MSAL.scopes, account }).subscribe({
      // Failures here are expected (3rd-party-cookie blocked iframe, IdP
      // hiccup); the next real request will retry through AuthRefreshInterceptor.
      error: () => undefined,
    });
  }

  private handleStorageEvent(event: StorageEvent): void {
    if (event.storageArea !== localStorage) return;
    // MSAL v5 stores every cache entry under a `msal.*` key; account list,
    // per-account credentials, access/refresh tokens, encryption metadata.
    // A removal of any of those fires here, but only the account-list key
    // disappearing is the "user logged out elsewhere" signal. The rest churn
    // routinely during silent refresh and used to cause spurious cross-tab
    // logouts.
    //
    // The account-list key is namespaced by cache schema version:
    //   • schema ≥ 1 (current default = 3) → `msal.<schema>.account.keys`
    //   • schema 0 (legacy) → `msal.account.keys`
    // (cf. @azure/msal-browser dist/cache/CacheKeys.mjs `getAccountKeysCacheKey`).
    // Match suffix + prefix instead of an exact string so a future schema bump
    // does not silently turn the cross-tab logout signal into a no-op.
    if (!event.key?.startsWith('msal.') || !event.key.endsWith('.account.keys')) {
      return;
    }
    if (event.newValue !== null) return;
    // Skip if the local tab is already logged out; avoids redirect loops.
    if (!this.msalService.instance.getActiveAccount()) return;
    // sessionStorage is per-tab: each tab records its own page, so a later
    // re-login in this tab returns here instead of the post-logout home.
    this.returnUrl.captureCurrent();
    this.authService.logout();
  }
}
